import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, ModalBody, Row, Col, Card, CardBody, Badge, Table, Button } from "reactstrap";
import { IoCloseSharp } from "react-icons/io5";
import { 
    FiBox, FiLink2, FiPlus, FiTrash2, FiImage, FiChevronUp, 
    FiChevronDown, FiLayers, FiTag, FiCheckCircle, FiSearch, FiInfo, FiMove,
    FiFolder, FiCornerDownRight
} from "react-icons/fi";
import { Select, Spin, Input, Tooltip, Tag, TreeSelect } from "antd";
import { getAllProducts, getAllManufacturers, getAllSubCategories, getAllCategories } from "../../api/pagination";
import { migrateToGroup } from "../../api/products.api";
import { useSelector, useDispatch } from "react-redux";
import { allManufacturers, AllCategories, AllSubCategories } from "../../store/actions/actions";
import { productTypes } from "../../data";
import { uploadImage, uniqueID, generateCustomID, saveData } from "../../firebase/utility";
import { FaPlus } from "react-icons/fa";
import AddNewManufacturer from "./AddNewManufacturer";
import debounce from "lodash/debounce";
import toast from "react-hot-toast";

const SUGGESTED_ATTRIBUTES = ["capacity", "color", "size", "speed", "interface", "condition"];

const BulkMigrateModal = ({ isOpen, toggle, refreshData }) => {
    const dispatch = useDispatch();
    const { all_manufacturers, all_categories, all_subCategories } = useSelector((state) => state.redux);

    const [groupType, setGroupType] = useState("variant");
    const [primaryProduct, setPrimaryProduct] = useState(null);
    const [groupName, setGroupName] = useState("");
    const [additionalProducts, setAdditionalProducts] = useState([]);
    
    // Categorization inherited from Primary Product
    const [groupMfgId, setGroupMfgId] = useState("");
    const [groupProductType, setGroupProductType] = useState("");
    const [groupSubCategoryId, setGroupSubCategoryId] = useState("");
    const [groupCategoryId, setGroupCategoryId] = useState("");

    const [mfg, setMfg] = useState(null);
    const [searchedMfg, setSearchMfg] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [mfgModal, setMfgModal] = useState(false);
    const [mfgSubmitLoading, setMfgSubmitLoading] = useState(false);

    // Attribute keys for variant group (e.g. ['capacity', 'color'])
    const [attributeKeys, setAttributeKeys] = useState(["capacity"]);
    const [newAttributeInput, setNewAttributeInput] = useState("");
    
    // Map of productId -> { attributeKey: value }
    const [productAttributes, setProductAttributes] = useState({});

    // Product search dropdown state
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [selectedSearchProduct, setSelectedSearchProduct] = useState(null);

    const [submitting, setSubmitting] = useState(false);

    // Ensure all manufacturers, categories, and subcategories are available in Redux
    useEffect(() => {
        if (!all_manufacturers || all_manufacturers.length === 0) {
            getAllManufacturers(1, "", 2000, (res) => {
                if (res?.status === 200 && res?.data?.data) {
                    dispatch(allManufacturers(res.data.data));
                }
            });
        }
        if (!all_categories || all_categories.length === 0) {
            getAllCategories(1, "", 2000, (res) => {
                if (res?.status === 200 && (res?.data?.data || res?.data)) {
                    dispatch(AllCategories(res.data.data || res.data));
                }
            });
        }
        if (!all_subCategories || all_subCategories.length === 0) {
            getAllSubCategories(1, "", 2000, (res) => {
                if (res?.status === 200 && (res?.data?.data || res?.data)) {
                    dispatch(AllSubCategories(res.data.data || res.data));
                }
            });
        }
    }, [all_manufacturers, all_categories, all_subCategories, dispatch]);

    const fetchManufacturersDebounced = useMemo(
        () =>
            debounce((searchText) => {
                if (!searchText || !searchText.trim()) {
                    setSearchMfg([]);
                    setDataLoading(false);
                    return;
                }
                getAllManufacturers(1, searchText, 50, (res) => {
                    if (res?.status === 200 && res?.data?.data) {
                        setSearchMfg(res.data.data);
                    }
                    setDataLoading(false);
                });
            }, 500),
        []
    );

    const handleManufacturerSearch = (searchText) => {
        if (!searchText || !searchText.trim()) {
            setSearchMfg([]);
            setDataLoading(false);
            return;
        }
        setDataLoading(true);
        fetchManufacturersDebounced(searchText);
    };

    const handleMfg = (val) => {
        let source = searchedMfg?.length > 0 ? searchedMfg : all_manufacturers;
        let res = source?.find((i) => (i?._id && String(i._id) === String(val)) || (i?.id && String(i.id) === String(val)));
        setMfg(res);
        setGroupMfgId(res?._id || res?.id || val);
    };

    // Manufacturer select options
    const mfgOptions = useMemo(() => {
        const source = searchedMfg?.length > 0 ? searchedMfg : all_manufacturers || [];
        const list = [...source];
        if (mfg && !list.some((m) => (m._id && String(m._id) === String(mfg._id)) || (m.id && String(m.id) === String(mfg.id)))) {
            list.unshift(mfg);
        }
        return list.map((option) => ({
            value: option._id || option.id,
            label: `${option.id ? `${option.id}-` : ""}${option.name || option.mfg_name || ""}`
        }));
    }, [searchedMfg, all_manufacturers, mfg]);

    // Helper to extract category IDs from subcategory object
    const getCategoryIdsFromSubCat = (s) => {
        const ids = [];
        if (!s) return ids;
        const addId = (val) => {
            if (val === undefined || val === null || val === "") return;
            if (typeof val === "object") {
                if (val._id) ids.push(String(val._id).trim().toLowerCase());
                if (val.id) ids.push(String(val.id).trim().toLowerCase());
                if (val.categoryName) ids.push(String(val.categoryName).trim().toLowerCase());
                if (val.name) ids.push(String(val.name).trim().toLowerCase());
            } else {
                ids.push(String(val).trim().toLowerCase());
            }
        };
        addId(s.categoryId);
        addId(s.category_id);
        addId(s.category);
        addId(s.parentCategory);
        addId(s.parent_category);
        addId(s.categoryName);
        return ids;
    };

    const isSubInCat = (sub, cat) => {
        if (!sub || !cat) return false;
        const subCatIds = getCategoryIdsFromSubCat(sub);
        const catKeys = [
            cat._id ? String(cat._id).trim().toLowerCase() : "",
            cat.id ? String(cat.id).trim().toLowerCase() : "",
            cat.categoryName ? String(cat.categoryName).trim().toLowerCase() : "",
            cat.category ? String(cat.category).trim().toLowerCase() : "",
            cat.name ? String(cat.name).trim().toLowerCase() : ""
        ].filter(Boolean);

        return catKeys.some((k) => subCatIds.includes(k));
    };

    // Helper to auto-resolve Product Type (Hardware/Software) from SubCategory
    const getProductTypeFromSubCat = (s, cats = all_categories, subs = all_subCategories) => {
        if (!s) return "";
        let sub = typeof s === "object" ? s : null;
        if (!sub && subs && subs.length > 0) {
            sub = subs.find((item) => (item._id && String(item._id) === String(s)) || (item.id && String(item.id) === String(s)));
        }
        if (sub) {
            if (sub.categoryType) {
                return String(sub.categoryType).toLowerCase() === "software" ? "Software" : "Hardware";
            }
            if (cats && cats.length > 0) {
                const matchedCat = cats.find((c) => isSubInCat(sub, c));
                if (matchedCat?.categoryType) {
                    return String(matchedCat.categoryType).toLowerCase() === "software" ? "Software" : "Hardware";
                }
            }
        }
        return "Hardware";
    };

    // Construct unified TreeData for Category & SubCategory (shows 100% of categories & subcategories)
    const categoryTreeData = useMemo(() => {
        const catList = Array.isArray(all_categories) ? all_categories : [];
        const subList = Array.isArray(all_subCategories) ? all_subCategories : [];

        if (catList.length === 0 && subList.length === 0) return [];

        const data = catList.map((cat) => {
            const catId = cat._id || cat.id;
            const catName = cat.categoryName || cat.category || cat.name || "Unnamed Category";
            const catType = cat.categoryType || cat.category_type || cat.type || "";
            const catKey = `cat_${catId}`;

            // Find subcategories belonging to this category
            const subCats = subList.filter((sub) => isSubInCat(sub, cat));

            return {
                title: (
                    <div
                        className="d-flex align-items-center justify-content-between py-1 pe-2 w-100"
                        style={{ minWidth: 0, gap: "10px" }}
                    >
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                            <FiFolder className="text-primary flex-shrink-0" size={14} />
                            {cat.id && (
                                <span
                                    className="badge bg-secondary-subtle text-secondary border font-monospace flex-shrink-0"
                                    style={{ fontSize: "10px", padding: "2px 5px", fontWeight: 600 }}
                                >
                                    #{cat.id}
                                </span>
                            )}
                            <span className="fw-semibold text-dark text-truncate" style={{ fontSize: "13px" }}>
                                {catName}
                            </span>
                        </div>
                        <span
                            className="badge bg-light text-secondary border flex-shrink-0"
                            style={{ fontSize: "10px", padding: "2px 6px", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" }}
                        >
                            Category
                        </span>
                    </div>
                ),
                label: `${cat.id ? `[#${cat.id}] ` : ""}${catName}`,
                value: catKey,
                key: catKey,
                searchValue: `${cat.id || ""} ${catName} ${catType} Category`,
                children: subCats.map((sub) => {
                    const subId = sub._id || sub.id;
                    const subName = sub.subCategoryName || sub.subCategory || sub.name || "Unnamed SubCategory";
                    const subKey = `sub_${subId}`;
                    return {
                        title: (
                            <div
                                className="d-flex align-items-center justify-content-between py-1 pe-2 ps-1 w-100"
                                style={{ minWidth: 0, gap: "10px" }}
                            >
                                <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                                    <FiCornerDownRight className="text-muted flex-shrink-0" size={12} />
                                    {sub.id && (
                                        <span
                                            className="badge bg-primary-subtle text-primary border border-primary-subtle font-monospace flex-shrink-0"
                                            style={{ fontSize: "10px", padding: "2px 5px", fontWeight: 600 }}
                                        >
                                            #{sub.id}
                                        </span>
                                    )}
                                    <span className="text-dark text-truncate" style={{ fontSize: "13px" }}>
                                        {subName}
                                    </span>
                                </div>
                                <span
                                    className="badge bg-primary-subtle text-primary border border-primary-subtle flex-shrink-0"
                                    style={{ fontSize: "10px", padding: "2px 6px", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" }}
                                >
                                    Sub Category
                                </span>
                            </div>
                        ),
                        label: `${sub.id ? `[#${sub.id}] ` : ""}${subName} (${catName})`,
                        value: subKey,
                        key: subKey,
                        searchValue: `${sub.id || ""} ${subName} ${catName} Sub Category`,
                    };
                }),
            };
        });

        // Add any subcategories not mapped to known categories
        const unmappedSubCats = subList.filter((sub) => !catList.some((cat) => isSubInCat(sub, cat)));
        if (unmappedSubCats.length > 0) {
            data.push({
                title: (
                    <div
                        className="d-flex align-items-center justify-content-between py-1 pe-2 w-100"
                        style={{ minWidth: 0, gap: "10px" }}
                    >
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                            <FiFolder className="text-secondary flex-shrink-0" size={14} />
                            <span className="fw-semibold text-dark text-truncate" style={{ fontSize: "13px" }}>
                                Other Subcategories
                            </span>
                        </div>
                        <span
                            className="badge bg-light text-secondary border flex-shrink-0"
                            style={{ fontSize: "10px", padding: "2px 6px", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" }}
                        >
                            Group
                        </span>
                    </div>
                ),
                label: "Other Subcategories",
                value: "group_unmapped",
                key: "group_unmapped",
                disabled: true,
                selectable: false,
                searchValue: "Other Subcategories",
                children: unmappedSubCats.map((sub) => {
                    const subId = sub._id || sub.id;
                    const subName = sub.subCategoryName || sub.subCategory || sub.name || "Unnamed SubCategory";
                    const subKey = `sub_${subId}`;

                    return {
                        title: (
                            <div
                                className="d-flex align-items-center justify-content-between py-1 pe-2 ps-1 w-100"
                                style={{ minWidth: 0, gap: "10px" }}
                            >
                                <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                                    <FiCornerDownRight className="text-muted flex-shrink-0" size={12} />
                                    {sub.id && (
                                        <span
                                            className="badge bg-primary-subtle text-primary border border-primary-subtle font-monospace flex-shrink-0"
                                            style={{ fontSize: "10px", padding: "2px 5px", fontWeight: 600 }}
                                        >
                                            #{sub.id}
                                        </span>
                                    )}
                                    <span className="text-dark text-truncate" style={{ fontSize: "13px" }}>
                                        {subName}
                                    </span>
                                </div>
                                <span
                                    className="badge bg-primary-subtle text-primary border border-primary-subtle flex-shrink-0"
                                    style={{ fontSize: "10px", padding: "2px 6px", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" }}
                                >
                                    Sub Category
                                </span>
                            </div>
                        ),
                        label: `${sub.id ? `[#${sub.id}] ` : ""}${subName}`,
                        value: subKey,
                        key: subKey,
                        searchValue: `${sub.id || ""} ${subName} Sub Category`,
                    };
                }),
            });
        }

        return data;
    }, [all_categories, all_subCategories]);

    // Selected value
    const selectedTreeValue = useMemo(() => {
        if (groupSubCategoryId) return `sub_${groupSubCategoryId}`;
        if (groupCategoryId) return `cat_${groupCategoryId}`;
        return undefined;
    }, [groupCategoryId, groupSubCategoryId]);

    // Handle single input selection
    const handleCategoryTreeChange = (val) => {
        if (!val) {
            setGroupCategoryId("");
            setGroupSubCategoryId("");
            return;
        }

        const valStr = String(val);
        if (valStr.startsWith("cat_")) {
            const catId = valStr.replace("cat_", "");
            const matchedCat = all_categories?.find(
                (c) => (c._id && String(c._id) === catId) || (c.id && String(c.id) === catId)
            );
            const autoProductType = matchedCat?.categoryType
                ? (String(matchedCat.categoryType).toLowerCase() === "software" ? "Software" : "Hardware")
                : "";

            setGroupCategoryId(catId);
            setGroupSubCategoryId("");
            if (autoProductType) setGroupProductType(autoProductType);
        } else if (valStr.startsWith("sub_")) {
            const subId = valStr.replace("sub_", "");
            const matchedSub = all_subCategories?.find(
                (s) => (s._id && String(s._id) === subId) || (s.id && String(s.id) === subId)
            );

            let matchedCatId = "";
            let matchedCat = null;
            if (matchedSub && all_categories?.length > 0) {
                matchedCat = all_categories.find((c) => isSubInCat(matchedSub, c));
                if (matchedCat) {
                    matchedCatId = matchedCat._id || matchedCat.id || "";
                }
            }

            const autoProductType = matchedCat?.categoryType
                ? (String(matchedCat.categoryType).toLowerCase() === "software" ? "Software" : "Hardware")
                : getProductTypeFromSubCat(matchedSub || subId, all_categories, all_subCategories);

            setGroupSubCategoryId(subId);
            setGroupCategoryId(matchedCatId || groupCategoryId);
            if (autoProductType) setGroupProductType(autoProductType);
        }
    };

    // Drag & drop state and handlers for reordering products
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const handleMoveProduct = (fromIdx, toIdx) => {
        if (fromIdx === 0 || toIdx === 0) return; // Keep primary product at top
        const fromAdditionalIdx = fromIdx - 1;
        const toAdditionalIdx = toIdx - 1;
        if (
            fromAdditionalIdx < 0 ||
            toAdditionalIdx < 0 ||
            fromAdditionalIdx >= additionalProducts.length ||
            toAdditionalIdx >= additionalProducts.length
        )
            return;
        setAdditionalProducts((prev) => {
            const next = [...prev];
            const [moved] = next.splice(fromAdditionalIdx, 1);
            next.splice(toAdditionalIdx, 0, moved);
            return next;
        });
    };

    const handleDragStart = (e, index) => {
        if (index === 0) {
            e.preventDefault();
            return;
        }
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (index !== 0 && dragOverIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDrop = (e, index) => {
        e.preventDefault();
        if (index !== 0 && draggedIndex !== null && draggedIndex !== index) {
            handleMoveProduct(draggedIndex, index);
        }
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleSubmitMfgData = async (obj, file, setData) => {
        setMfgSubmitLoading(true);
        let data = {
            _id: uniqueID(),
            ...obj,
            createdAt: Date.parse(new Date()),
            id: await generateCustomID("Manufacturers", "id"),
        };
        let losFile;
        if (file !== undefined && typeof file === "object") {
            losFile = await uploadImage(file, "manufacturers");
            data.image = losFile;
        }
        saveData("AllowedManufacturers", data._id, data).then(() => {
            if (setData) {
                setMfg(data);
                setGroupMfgId(data._id);
                setSearchMfg(prev => [data, ...prev]);
            }
            setMfgSubmitLoading(false);
        }).catch(() => setMfgSubmitLoading(false));
    };

    // Auto-select Manufacturer, Product Type, Subcategory, Category when Primary Product is selected
    const handleSelectPrimaryProduct = (product) => {
        if (!product) {
            setPrimaryProduct(null);
            setGroupName("");
            setGroupMfgId("");
            setGroupProductType("");
            setGroupSubCategoryId("");
            setGroupCategoryId("");
            setMfg(null);
            return;
        }
        setPrimaryProduct(product);
        setGroupName(product.title || product.name || "");
        const pId = product._id || product.id;
        setAdditionalProducts(prev => prev.filter(p => (p._id || p.id) !== pId));

        const extractId = (val) => {
            if (!val && val !== 0) return "";
            if (typeof val === "string" || typeof val === "number") return String(val).trim();
            if (typeof val === "object") return String(val._id || val.id || "").trim();
            return "";
        };

        const mfgId = extractId(product.mfg) || extractId(product.manufacturer) || extractId(product.mfg_id) || extractId(product.mfgId);
        const catId = extractId(product.category) || extractId(product.categoryId);
        const subCatId = extractId(product.subCategory) || extractId(product.subCategoryId);

        const matchedSub = all_subCategories?.find(s => 
            (s._id && String(s._id) === String(subCatId)) || 
            (s.id && String(s.id) === String(subCatId))
        ) || (typeof product.subCategory === "object" ? product.subCategory : null);
        
        const resolvedCatId = catId || extractId(matchedSub?.categoryId) || extractId(matchedSub?.category) || "";
        
        const matchedCat = all_categories?.find(c => 
            (c._id && String(c._id) === String(resolvedCatId)) || 
            (c.id && String(c.id) === String(resolvedCatId))
        ) || (typeof product.category === "object" ? product.category : null);

        let pType = product.productType || "";
        if (!pType) {
            if (matchedCat?.categoryType) {
                pType = matchedCat.categoryType;
            } else if (matchedSub?.categoryType) {
                pType = matchedSub.categoryType;
            }
        }
        if (pType) {
            pType = pType.toLowerCase() === "software" ? "Software" : "Hardware";
        }

        setGroupMfgId(mfgId);
        setGroupProductType(pType);
        setGroupSubCategoryId(matchedSub?._id || matchedSub?.id || subCatId);
        setGroupCategoryId(matchedCat?._id || matchedCat?.id || resolvedCatId);

        // Match Manufacturer object for Select label
        const source = searchedMfg?.length > 0 ? searchedMfg : all_manufacturers;
        const foundMfg = source?.find(m => 
            (m._id && String(m._id) === String(mfgId)) || 
            (m.id && String(m.id) === String(mfgId)) || 
            (m.name && m.name.toLowerCase() === String(mfgId).toLowerCase())
        );
        if (foundMfg) {
            setMfg(foundMfg);
        } else if (product.mfg && typeof product.mfg === "object") {
            setMfg(product.mfg);
        } else if (product.manufacturer && typeof product.manufacturer === "object") {
            setMfg(product.manufacturer);
        } else if (product.mfg_name) {
            setMfg({ _id: mfgId, name: product.mfg_name });
        } else if (mfgId) {
            getAllManufacturers(1, mfgId, 5, (res) => {
                if (res?.status === 200 && res?.data?.data?.length > 0) {
                    const match = res.data.data.find(m => 
                        (m._id && String(m._id) === String(mfgId)) || 
                        (m.id && String(m.id) === String(mfgId))
                    );
                    if (match) setMfg(match);
                }
            });
        }
    };

    // Fetch initial / searched products for selection
    const searchProducts = (query = "") => {
        setSearchLoading(true);
        getAllProducts(1, query, 30, "publish", (res) => {
            if (res.status === 200) {
                const data = res?.data?.data || res?.data || [];
                setSearchResults(data);
            }
            setSearchLoading(false);
        });
    };

    useEffect(() => {
        if (isOpen) {
            searchProducts("");
        }
    }, [isOpen]);

    const handleClose = () => {
        setGroupType("variant");
        setPrimaryProduct(null);
        setGroupName("");
        setAdditionalProducts([]);
        setGroupMfgId("");
        setGroupProductType("");
        setGroupSubCategoryId("");
        setGroupCategoryId("");
        setMfg(null);
        setSearchMfg([]);
        setAttributeKeys(["capacity"]);
        setNewAttributeInput("");
        setProductAttributes({});
        setSearchResults([]);
        setSelectedSearchProduct(null);
        setSubmitting(false);
        toggle();
    };

    // Add a new attribute key
    const handleAddAttributeKey = (customKey) => {
        const key = (customKey || newAttributeInput).trim().toLowerCase();
        if (!key) return;
        if (attributeKeys.includes(key)) {
            toast.error(`Attribute "${key}" already added.`);
            return;
        }
        setAttributeKeys([...attributeKeys, key]);
        setNewAttributeInput("");
    };

    // Remove an attribute key
    const handleRemoveAttributeKey = (keyToRemove) => {
        if (attributeKeys.length <= 1) {
            toast.error("At least one attribute is required for variant groups.");
            return;
        }
        setAttributeKeys(attributeKeys.filter(k => k !== keyToRemove));
        
        // Clean up stored values for that key
        setProductAttributes(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(pId => {
                if (updated[pId]) {
                    const copy = { ...updated[pId] };
                    delete copy[keyToRemove];
                    updated[pId] = copy;
                }
            });
            return updated;
        });
    };

    // Update an attribute value for a specific product
    const handleProductAttributeChange = (productId, key, value) => {
        setProductAttributes(prev => ({
            ...prev,
            [productId]: {
                ...(prev[productId] || {}),
                [key]: value
            }
        }));
    };

    // Add an additional product to the migration list
    const handleAddAdditionalProduct = (product) => {
        if (!product) return;
        const productId = product._id || product.id;
        const primaryId = primaryProduct?._id || primaryProduct?.id;

        if (productId === primaryId) {
            toast.error("This product is already selected as the Primary Product.");
            return;
        }

        if (additionalProducts.some(p => (p._id || p.id) === productId)) {
            toast.error("This product is already in the list.");
            return;
        }

        setAdditionalProducts([...additionalProducts, product]);
        setSelectedSearchProduct(null);
    };

    // Remove an additional product
    const handleRemoveAdditionalProduct = (productId) => {
        setAdditionalProducts(additionalProducts.filter(p => (p._id || p.id) !== productId));
        setProductAttributes(prev => {
            const updated = { ...prev };
            delete updated[productId];
            return updated;
        });
    };

    // Reorder combined list
    const allProducts = primaryProduct ? [primaryProduct, ...additionalProducts] : [];

    const handleSubmit = () => {
        if (!primaryProduct) {
            toast.error("Please select a Primary Product.");
            return;
        }

        if (additionalProducts.length === 0) {
            toast.error("Please add at least one additional product to link into the group.");
            return;
        }

        const primaryId = primaryProduct._id || primaryProduct.id;
        const productIds = additionalProducts.map(p => p._id || p.id);

        let variantAttributesPayload = [];

        if (groupType === "variant") {
            // Validate that attribute values are filled
            for (let p of allProducts) {
                const pId = p._id || p.id;
                const attrs = productAttributes[pId] || {};
                for (let key of attributeKeys) {
                    if (!attrs[key] || !attrs[key].trim()) {
                        toast.error(`Please provide "${key}" for product: ${p.title || p.name}`);
                        return;
                    }
                }
            }

            variantAttributesPayload = allProducts.map((p, idx) => {
                const pId = p._id || p.id;
                return {
                    productId: pId,
                    attributes: productAttributes[pId] || {},
                    sortOrder: idx
                };
            });
        }

        const payload = {
            groupType,
            primaryProductId: primaryId,
            productIds,
            mfg_id: groupMfgId || "",
            productType: groupProductType || "",
            categoryId: groupCategoryId || "",
            category: groupCategoryId ? { _id: groupCategoryId } : undefined,
            subCategoryId: groupSubCategoryId || "",
            subCategory: groupSubCategoryId ? { _id: groupSubCategoryId } : undefined,
            name: (groupName || "").trim() || primaryProduct.title || primaryProduct.name || "",
            ...(groupType === "variant" && { variantAttributes: variantAttributesPayload })
        };

        setSubmitting(true);
        migrateToGroup(payload, (res) => {
            setSubmitting(false);
            if (res.status === 200 || res.status === 201 || res?.success) {
                toast.success(res.message || "Group created and products linked successfully!");
                refreshData();
                handleClose();
            } else {
                toast.error(res.message || "Failed to migrate products to group.");
            }
        });
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} size="xl" centered>
            <ModalBody className="p-4 bg-white">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                    <div className="d-flex align-items-center gap-2">
                        <div className="bg-primary-soft p-2 rounded-3 text-primary">
                            <FiLayers size={22} />
                        </div>
                        <div>
                            <h4 className="primary-color fw-bold m-0">Bulk Migrate to Product Group</h4>
                            <p className="text-muted small mb-0">Create a group from a primary product and link multiple variants/related items</p>
                        </div>
                    </div>
                    <IoCloseSharp size={28} className="pointer text-muted" onClick={handleClose} />
                </div>

                {/* Step 1: Select Group Type */}
                <Card className="border-0 shadow-sm mb-4 bg-light">
                    <CardBody className="p-3">
                        <label className="fw-bold mb-2 text-dark small text-uppercase tracking-wider">
                            1. Select Group Type
                        </label>
                        <Row className="g-3">
                            <Col md={6}>
                                <div 
                                    className={`p-3 rounded border text-start pointer transition-all ${groupType === 'variant' ? 'border-primary bg-white shadow-sm' : 'border-secondary-subtle bg-light text-muted'}`}
                                    onClick={() => setGroupType("variant")}
                                    style={{ cursor: "pointer", borderLeftWidth: groupType === 'variant' ? '4px' : '1px' }}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                        <div className="d-flex align-items-center gap-2">
                                            <FiBox size={20} className={groupType === 'variant' ? 'text-primary' : 'text-muted'} />
                                            <span className="fw-bold text-dark">Variant Group</span>
                                        </div>
                                        {groupType === 'variant' && <FiCheckCircle className="text-primary" />}
                                    </div>
                                    <p className="small mb-0 text-muted" style={{ fontSize: '12px' }}>
                                        Products with custom attribute differences (e.g. Capacity, Color, Size, Speed).
                                    </p>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div 
                                    className={`p-3 rounded border text-start pointer transition-all ${groupType === 'related' ? 'border-success bg-white shadow-sm' : 'border-secondary-subtle bg-light text-muted'}`}
                                    onClick={() => setGroupType("related")}
                                    style={{ cursor: "pointer", borderLeftWidth: groupType === 'related' ? '4px' : '1px' }}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                        <div className="d-flex align-items-center gap-2">
                                            <FiLink2 size={20} className={groupType === 'related' ? 'text-success' : 'text-muted'} />
                                            <span className="fw-bold text-dark">Related Group</span>
                                        </div>
                                        {groupType === 'related' && <FiCheckCircle className="text-success" />}
                                    </div>
                                    <p className="small mb-0 text-muted" style={{ fontSize: '12px' }}>
                                        Grouping of complementary or frequently bought together products (e.g. Accessories).
                                    </p>
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Step 2: Primary Product Selection */}
                <Card className="border-0 shadow-sm mb-4 bg-light">
                    <CardBody className="p-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="fw-bold text-dark small text-uppercase tracking-wider m-0">
                                2. Select Primary Product *
                            </label>
                            <span className="text-muted small" style={{ fontSize: '11px' }}>
                                The group's title, image, and category will inherit from this product
                            </span>
                        </div>
                        <Select
                            size="large"
                            showSearch
                            placeholder="Search primary product by title or SKU..."
                            className="w-100 mb-2"
                            filterOption={false}
                            onSearch={searchProducts}
                            notFoundContent={searchLoading ? <Spin size="small" /> : null}
                            value={primaryProduct ? (primaryProduct._id || primaryProduct.id) : undefined}
                            onChange={(val, option) => {
                                const selected = searchResults.find(p => (p._id || p.id) === val);
                                if (selected) {
                                    handleSelectPrimaryProduct(selected);
                                }
                            }}
                            options={searchResults.map(p => ({
                                value: p._id || p.id,
                                label: `${p.title || p.name || 'Unnamed Product'} (${p.mfgPartNumber || p.sku || 'No SKU'})`,
                                product: p
                            }))}
                            optionRender={(option) => {
                                const p = option.data.product;
                                return (
                                    <div className="d-flex align-items-center gap-3 py-1">
                                        {p?.imageUrl || p?.image || p?.images?.[0] ? (
                                             <img src={p.imageUrl || p.image || p.images[0]} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} className="rounded border bg-white flex-shrink-0" />
                                        ) : (
                                            <div className="bg-light border rounded d-flex align-items-center justify-content-center text-muted flex-shrink-0" style={{ width: 32, height: 32 }}>
                                                <FiImage size={16} />
                                            </div>
                                        )}
                                        <div className="d-flex flex-column text-truncate" style={{ minWidth: 0 }}>
                                            <span className="fw-bold text-truncate" style={{ fontSize: '13px', lineHeight: '1.3' }}>{p?.title || p?.name || 'Unnamed Product'}</span>
                                            <span className="text-muted" style={{ fontSize: '11px', lineHeight: '1.2' }}>SKU: {p?.mfgPartNumber || p?.sku || p?.id || 'N/A'} • ${p?.price || 0}</span>
                                        </div>
                                    </div>
                                );
                            }}
                        />

                        {primaryProduct && (
                            <>
                                <div className="p-3 bg-white rounded border d-flex align-items-center justify-content-between mt-2">
                                    <div className="d-flex align-items-center gap-3">
                                        {primaryProduct.imageUrl || primaryProduct.image || primaryProduct.images?.[0] ? (
                                            <img src={primaryProduct.imageUrl || primaryProduct.image || primaryProduct.images[0]} alt="" style={{ width: 45, height: 45, objectFit: 'contain' }} className="rounded border p-1" />
                                        ) : (
                                            <div className="bg-light border rounded d-flex align-items-center justify-content-center text-muted" style={{ width: 45, height: 45 }}>
                                                <FiImage size={20} />
                                            </div>
                                        )}
                                        <div>
                                            <div className="d-flex align-items-center gap-2">
                                                <h6 className="fw-bold text-dark mb-0">{primaryProduct.title || primaryProduct.name}</h6>
                                                <Badge color="primary" pill>PRIMARY</Badge>
                                            </div>
                                            <div className="text-muted small mt-1">
                                                <span>SKU: {primaryProduct.mfgPartNumber || primaryProduct.sku || primaryProduct.id}</span>
                                                <span className="mx-2">•</span>
                                                <span>Price: ${primaryProduct.price || 0}</span>
                                                <span className="mx-2">•</span>
                                                <span>Manufacturer: {primaryProduct.mfg_name || primaryProduct.manufacturer?.name || mfg?.name || "N/A"}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <Button color="light" size="sm" onClick={() => handleSelectPrimaryProduct(null)}>
                                        Change
                                    </Button>
                                </div>
                                <div className="mt-3 p-3 bg-white rounded border">
                                    <label className="fw-bold mb-1 text-dark" style={{ fontSize: "13px" }}>
                                        {groupType === "related" ? "Related Group Name *" : "Group Name *"}
                                    </label>
                                    <Input
                                        size="large"
                                        value={groupName}
                                        placeholder={groupType === "related" ? "e.g. Dell Latitude 5530 Accessories & Related Items" : "e.g. Samsung 990 PRO Series SSD"}
                                        onChange={(e) => setGroupName(e.target.value)}
                                    />
                                </div>
                            </>
                        )}
                    </CardBody>
                </Card>

                {/* Step 3: Manufacturer & Categorization (Inherited from Primary Product) */}
                <Card className="border-0 shadow-sm mb-4 bg-light">
                    <CardBody className="p-3">
                        <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                            <div className="bg-primary-soft p-2 rounded-3 text-primary">
                                <FiLayers size={16} />
                            </div>
                            <div>
                                <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-wider" style={{ fontSize: '13px' }}>
                                    3. Manufacturer & Categorization (Inherited from Primary Product)
                                </h6>
                                <small className="text-muted">Auto-selected from Primary Product; can be adjusted if needed.</small>
                            </div>
                        </div>
                        <Row className="g-3">
                            <Col md={4}>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <label className="fw-bold mb-0" style={{ fontSize: "13px" }}>Manufacturer</label>
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 text-primary small text-decoration-none"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setMfgModal(true);
                                        }}
                                    >
                                        + New Mfg
                                    </button>
                                </div>
                                <Select
                                    size="large"
                                    showSearch
                                    placeholder="Select Manufacturer"
                                    filterOption={false}
                                    onSelect={(val) => {
                                        handleMfg(val);
                                    }}
                                    value={groupMfgId || undefined}
                                    onSearch={handleManufacturerSearch}
                                    className="w-100"
                                    getPopupContainer={(trigger) => trigger.parentNode}
                                    options={mfgOptions}
                                    loading={dataLoading}
                                    notFoundContent={
                                        dataLoading ? <Spin size="small" /> : "No manufacturers found"
                                    }
                                />
                            </Col>
                            <Col md={4}>
                                <label className="fw-bold mb-1" style={{ fontSize: "13px" }}>Product Type *</label>
                                <Select
                                    size="large"
                                    placeholder="Select Product Type"
                                    className="w-100 drop_down_style"
                                    value={groupProductType || undefined}
                                    options={productTypes}
                                    onChange={(val) => { 
                                        setGroupProductType(val); 
                                    }}
                                />
                            </Col>
                            <Col md={4}>
                                <label className="fw-bold mb-1" style={{ fontSize: "13px" }}>Category / Sub Category *</label>
                                <TreeSelect
                                    size="large"
                                    showSearch
                                    allowClear
                                    treeDefaultExpandAll
                                    treeNodeLabelProp="label"
                                    placeholder="Select Category or Sub Category"
                                    value={selectedTreeValue}
                                    onChange={handleCategoryTreeChange}
                                    className="w-100"
                                    popupMatchSelectWidth={false}
                                    dropdownMatchSelectWidth={false}
                                    getPopupContainer={(trigger) => trigger.parentNode}
                                    dropdownStyle={{
                                        minWidth: 400,
                                        maxWidth: 540,
                                        maxHeight: 400,
                                        overflowY: "auto",
                                        borderRadius: "8px",
                                        padding: "6px 8px",
                                        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)"
                                    }}
                                    treeData={categoryTreeData}
                                    filterTreeNode={(input, treeNode) => {
                                        const searchTarget = (treeNode?.searchValue || treeNode?.label || "").toLowerCase();
                                        const tokens = input.toLowerCase().trim().split(/\s+/).filter(Boolean);
                                        return tokens.every((token) => searchTarget.includes(token));
                                    }}
                                />
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Step 4: Add Additional Products */}
                <Card className="border-0 shadow-sm mb-4 bg-light">
                    <CardBody className="p-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="fw-bold text-dark small text-uppercase tracking-wider m-0">
                                4. Add Additional Products to Include ({additionalProducts.length} added)
                            </label>
                        </div>
                        <div className="d-flex gap-2 mb-3">
                            <div className="flex-grow-1">
                                <Select
                                    size="large"
                                    showSearch
                                    placeholder="Search and select products to link..."
                                    className="w-100"
                                    filterOption={false}
                                    onSearch={searchProducts}
                                    notFoundContent={searchLoading ? <Spin size="small" /> : null}
                                    value={selectedSearchProduct}
                                    onChange={(val) => {
                                        const selected = searchResults.find(p => (p._id || p.id) === val);
                                        if (selected) {
                                            handleAddAdditionalProduct(selected);
                                        }
                                    }}
                                    options={searchResults
                                        .filter(p => {
                                            const pId = p._id || p.id;
                                            const primaryId = primaryProduct?._id || primaryProduct?.id;
                                            const alreadyAdded = additionalProducts.some(ap => (ap._id || ap.id) === pId);
                                            return pId !== primaryId && !alreadyAdded;
                                        })
                                        .map(p => ({
                                            value: p._id || p.id,
                                            label: `${p.title || p.name || 'Unnamed Product'} (${p.mfgPartNumber || p.sku || 'No SKU'})`,
                                            product: p
                                        }))}
                                    optionRender={(option) => {
                                        const p = option.data.product;
                                        return (
                                            <div className="d-flex align-items-center gap-3 py-1">
                                                {p?.imageUrl || p?.image || p?.images?.[0] ? (
                                                    <img src={p.imageUrl || p.image || p.images[0]} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} className="rounded border bg-white flex-shrink-0" />
                                                ) : (
                                                    <div className="bg-light border rounded d-flex align-items-center justify-content-center text-muted flex-shrink-0" style={{ width: 32, height: 32 }}>
                                                        <FiImage size={16} />
                                                    </div>
                                                )}
                                                <div className="d-flex flex-column text-truncate" style={{ minWidth: 0 }}>
                                                    <span className="fw-bold text-truncate" style={{ fontSize: '13px', lineHeight: '1.3' }}>{p?.title || p?.name || 'Unnamed Product'}</span>
                                                    <span className="text-muted" style={{ fontSize: '11px', lineHeight: '1.2' }}>SKU: {p?.mfgPartNumber || p?.sku || p?.id || 'N/A'} • ${p?.price || 0}</span>
                                                </div>
                                            </div>
                                        );
                                    }}
                                />
                            </div>
                        </div>

                        {/* Step 5: Attribute Config for Variant Type */}
                        {groupType === "variant" && (
                            <div className="bg-white p-3 rounded border mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <label className="fw-bold text-dark small text-uppercase tracking-wider m-0">
                                        5. Variant Attribute Keys (e.g. Capacity, Color, Size)
                                    </label>
                                    <div className="d-flex align-items-center gap-1">
                                        <span className="text-muted small me-1" style={{ fontSize: '11px' }}>Suggestions:</span>
                                        {SUGGESTED_ATTRIBUTES.filter(s => !attributeKeys.includes(s)).slice(0, 4).map(s => (
                                            <Tag 
                                                key={s} 
                                                className="pointer text-capitalize border" 
                                                style={{ cursor: 'pointer', fontSize: '11px' }}
                                                onClick={() => handleAddAttributeKey(s)}
                                            >
                                                + {s}
                                            </Tag>
                                        ))}
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                                    {attributeKeys.map(attr => (
                                        <Tag 
                                            key={attr} 
                                            closable={attributeKeys.length > 1} 
                                            onClose={() => handleRemoveAttributeKey(attr)}
                                            color="blue"
                                            className="px-2 py-1 text-capitalize fw-bold"
                                        >
                                            {attr}
                                        </Tag>
                                    ))}
                                </div>
                                <div className="d-flex gap-2" style={{ maxWidth: '350px' }}>
                                    <Input
                                        placeholder="Add custom attribute..."
                                        value={newAttributeInput}
                                        onChange={(e) => setNewAttributeInput(e.target.value)}
                                        onPressEnter={() => handleAddAttributeKey()}
                                    />
                                    <Button color="primary" outline size="sm" onClick={() => handleAddAttributeKey()}>
                                        <FiPlus /> Add
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Step 6: Table of All Products to be Linked */}
                        {allProducts.length > 0 ? (
                            <div className="table-responsive bg-white rounded border">
                                <Table hover className="align-middle mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th style={{ width: "60px" }}>#</th>
                                            <th>Product Info</th>
                                            <th>Price</th>
                                            {groupType === "variant" && attributeKeys.map(attr => (
                                                <th key={attr} className="text-capitalize" style={{ minWidth: "140px" }}>
                                                    {attr} *
                                                </th>
                                            ))}
                                            <th style={{ width: "100px" }} className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allProducts.map((product, idx) => {
                                            const isPrimary = idx === 0;
                                            const pId = product._id || product.id;
                                            const isDragging = draggedIndex === idx;
                                            const isDragOver = dragOverIndex === idx;
                                            return (
                                                <tr
                                                    key={pId || idx}
                                                    className={isPrimary ? "table-primary bg-opacity-10" : ""}
                                                    draggable={!isPrimary}
                                                    onDragStart={(e) => handleDragStart(e, idx)}
                                                    onDragOver={(e) => handleDragOver(e, idx)}
                                                    onDrop={(e) => handleDrop(e, idx)}
                                                    onDragEnd={handleDragEnd}
                                                    style={{
                                                        cursor: isPrimary ? "default" : "grab",
                                                        opacity: isDragging ? 0.35 : 1,
                                                        backgroundColor: isDragOver ? "#e8f4fd" : undefined,
                                                        borderTop: isDragOver ? "2px solid #0d6efd" : undefined,
                                                        transition: "background-color 0.15s ease"
                                                    }}
                                                >
                                                    <td>
                                                        <div className="d-flex align-items-center gap-1">
                                                            {!isPrimary && (
                                                                <FiMove size={14} className="text-muted pointer me-1" title="Drag to reorder" />
                                                            )}
                                                            <Badge color={isPrimary ? "primary" : "light"} className={isPrimary ? "" : "text-dark border"}>
                                                                {idx + 1}
                                                            </Badge>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            {product.imageUrl || product.image || product.images?.[0] ? (
                                                                <img src={product.imageUrl || product.image || product.images[0]} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} className="rounded border bg-white" />
                                                            ) : (
                                                                <div className="bg-light border rounded d-flex align-items-center justify-content-center text-muted" style={{ width: 32, height: 32 }}>
                                                                    <FiImage size={16} />
                                                                </div>
                                                            )}
                                                            <div>
                                                                <div className="fw-bold text-dark d-flex align-items-center gap-1">
                                                                    {product.title || product.name || "Unnamed"}
                                                                    {isPrimary && <Badge color="primary" pill style={{ fontSize: '9px' }}>PRIMARY</Badge>}
                                                                </div>
                                                                <div className="text-muted small" style={{ fontSize: '11px' }}>
                                                                    SKU: {product.mfgPartNumber || product.sku || product.id}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>${product.price || 0}</td>
                                                    {groupType === "variant" && attributeKeys.map(attr => (
                                                        <td key={attr}>
                                                            <Input
                                                                placeholder={`e.g. ${attr === 'capacity' ? (idx === 0 ? '1TB' : '2TB') : 'Value'}`}
                                                                value={productAttributes[pId]?.[attr] || ""}
                                                                onChange={(e) => handleProductAttributeChange(pId, attr, e.target.value)}
                                                                className="w-100"
                                                            />
                                                        </td>
                                                    ))}
                                                    <td className="text-end">
                                                        <div className="d-flex align-items-center justify-content-end gap-1">
                                                            {!isPrimary && (
                                                                <>
                                                                    <Button
                                                                        color="light"
                                                                        size="sm"
                                                                        className="p-1 text-muted border-0 shadow-none"
                                                                        disabled={idx <= 1}
                                                                        onClick={() => handleMoveProduct(idx, idx - 1)}
                                                                        title="Move Up"
                                                                    >
                                                                        <FiChevronUp size={16} />
                                                                    </Button>
                                                                    <Button
                                                                        color="light"
                                                                        size="sm"
                                                                        className="p-1 text-muted border-0 shadow-none"
                                                                        disabled={idx === allProducts.length - 1}
                                                                        onClick={() => handleMoveProduct(idx, idx + 1)}
                                                                        title="Move Down"
                                                                    >
                                                                        <FiChevronDown size={16} />
                                                                    </Button>
                                                                    <Button
                                                                        color="link"
                                                                        className="text-danger p-1 ms-1"
                                                                        onClick={() => handleRemoveAdditionalProduct(pId)}
                                                                        title="Remove"
                                                                    >
                                                                        <FiTrash2 size={16} />
                                                                    </Button>
                                                                </>
                                                            )}
                                                            {isPrimary && (
                                                                <span className="text-muted small fst-italic">Primary</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-4 bg-white rounded border text-muted">
                                <FiBox size={30} className="mb-2 text-secondary" />
                                <p className="mb-0">Select a primary product above to begin configuring the group.</p>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Footer Buttons */}
                <div className="d-flex justify-content-between align-items-center pt-3 border-top">
                    <div className="text-muted small">
                        {allProducts.length} total product{allProducts.length !== 1 ? 's' : ''} will be linked in this group.
                    </div>
                    <div className="d-flex gap-2">
                        <Button color="light" onClick={handleClose} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button 
                            color="primary" 
                            className="px-4 shadow-sm fw-bold d-flex align-items-center gap-2" 
                            onClick={handleSubmit} 
                            disabled={submitting || !primaryProduct || additionalProducts.length === 0}
                        >
                            {submitting ? <Spin size="small" /> : <FiLayers />}
                            <span>Migrate & Create Group</span>
                        </Button>
                    </div>
                </div>
            </ModalBody>

            <AddNewManufacturer
                isOpen={mfgModal}
                toggle={() => setMfgModal(!mfgModal)}
                handleSubmitMfgData={handleSubmitMfgData}
                mfgSubmitLoading={mfgSubmitLoading}
            />
        </Modal>
    );
};

export default BulkMigrateModal;
